// NOMBASE — PostHog initialisation (frontend).
//
// Called from index.js AFTER the runtime config (lib/runtime-config.js)
// has resolved. The init key + host are read from runtime config
// (Doppler → /api/config/public → frontend fetch on boot). Build-time
// REACT_APP_POSTHOG_KEY remains a fallback inside lib/runtime-config.js.
//
// Privacy posture:
//   • person_profiles: 'identified_only' — anonymous users don't
//     create person records in PostHog (cheaper + cleaner data)
//   • Session Replay: OFF by default — must be opted in via Doppler
//     (POSTHOG_SESSION_RECORDING=true). It's the single biggest
//     client-side perf cost in a heavy SPA, so we don't pay it
//     unless an operator explicitly enables it. When enabled, a
//     sample_rate caps the overhead.
//   • Autocapture: OFF by default — same reason. Opt in via
//     POSTHOG_AUTOCAPTURE=true.
import posthog from 'posthog-js';

let initialised = false;

export function initPostHog({
  key,
  host,
  sessionRecording = false,
  sessionRecordingSampleRate = 0.1,
  autocapture = false,
} = {}) {
  if (initialised || !key) return posthog;
  try {
    const config = {
      api_host: host || 'https://us.i.posthog.com',
      person_profiles: 'identified_only',
      autocapture,
      capture_pageview: true,
      capture_pageleave: true,
      // Hard-disable session recording unless explicitly enabled.
      // posthog-js evaluates `disable_session_recording` first; when
      // true, the recorder is never instantiated and the heavy
      // rrweb script is not even fetched.
      disable_session_recording: !sessionRecording,
      loaded: () => {
        // eslint-disable-next-line no-console
        console.info('[PostHog] initialised', {
          host,
          autocapture,
          session_recording: sessionRecording,
        });
      },
    };
    if (sessionRecording) {
      config.session_recording = {
        maskAllInputs: true,
        sampleRate: sessionRecordingSampleRate,
        maskInputFn: (text /* el */) => {
          if (typeof text !== 'string') return text;
          return text
            // emails
            .replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[EMAIL]')
            // credit-card-ish 16-digit groups
            .replace(/\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}/g, '[CARD]')
            // SSN
            .replace(/\d{3}-\d{2}-\d{4}/g, '[SSN]');
        },
      };
    }
    posthog.init(key, config);
    initialised = true;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[PostHog] init failed', e);
  }
  return posthog;
}

export function isPosthogEnabled() {
  return initialised;
}

export default posthog;
